import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Inventory from "@/pages/Inventory";
import AddYarn from "@/pages/AddYarn";
import EditYarn from "@/pages/EditYarn";
import YarnDetail from "@/pages/YarnDetail";
import Projects from "@/pages/Projects";
import AddProject from "@/pages/AddProject";
import EditProject from "@/pages/EditProject";
import ProjectDetail from "@/pages/ProjectDetail";
import ShoppingList from "@/pages/ShoppingList";
import Patterns from "@/pages/Patterns";
import AddPattern from "@/pages/AddPattern";
import PatternDetail from "@/pages/PatternDetail";
import Marketplace from "@/pages/Marketplace";
import Messages from "@/pages/Messages";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Support from "@/pages/Support";
import SupportThankYou from "@/pages/SupportThankYou";
import Designs from "@/pages/Designs";
import DesignDetail from "@/pages/DesignDetail";
import DesignThankYou from "@/pages/DesignThankYou";
import AdminDesigns from "@/pages/AdminDesigns";
import AdminDesignForm from "@/pages/AdminDesignForm";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import AdminBlog from "@/pages/AdminBlog";
import AdminBlogForm from "@/pages/AdminBlogForm";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminTranslations from "@/pages/AdminTranslations";
import AdminLegalPages from "@/pages/AdminLegalPages";
import AdminUsers from "@/pages/AdminUsers";
import Unsubscribe from "@/pages/Unsubscribe";
import NewsletterConfirm from "@/pages/NewsletterConfirm";
import Login from "@/pages/Login";
import AuthVerify from "@/pages/AuthVerify";
import Profile from "@/pages/Profile";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CookiePolicy from "@/pages/CookiePolicy";
import Imprint from "@/pages/Imprint";
import RefundPolicy from "@/pages/RefundPolicy";
import Statistics from "@/pages/Statistics";
import PublicStash from "@/pages/PublicStash";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieConsent from "@/components/CookieConsent";
import GoogleAnalytics from "@/components/GoogleAnalytics";
import SkipLink from "@/components/SkipLink";
import { FormErrorProvider } from "@/components/FormErrorAnnouncer";
import { Toaster } from "@/components/ui/sonner";

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      {/* Public Design Shop Routes */}
      <Route path="/designs" element={<Designs />} />
      <Route path="/designs/:designId" element={<DesignDetail />} />
      <Route path="/designs/:designId/thank-you" element={<DesignThankYou />} />
      {/* Public Blog Routes */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/category/:categorySlug" element={<Blog />} />
      <Route path="/blog/:postSlug" element={<BlogPost />} />
      {/* Unsubscribe Route */}
      <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
      {/* Newsletter Confirmation Route (Double Opt-in) */}
      <Route path="/newsletter/confirm/:token" element={<NewsletterConfirm />} />
      {/* Login Page */}
      <Route path="/login" element={<Login />} />
      {/* Magic Link Verification */}
      <Route path="/auth/verify" element={<AuthVerify />} />
      {/* User Profile */}
      <Route path="/profile" element={<Profile />} />
      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/imprint" element={<Imprint />} />
      <Route path="/refund" element={<RefundPolicy />} />
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
      <Route path="/inventory/add" element={<ProtectedRoute><AddYarn /></ProtectedRoute>} />
      <Route path="/inventory/:yarnId/edit" element={<ProtectedRoute><EditYarn /></ProtectedRoute>} />
      <Route path="/inventory/:yarnId" element={<ProtectedRoute><YarnDetail /></ProtectedRoute>} />
      <Route path="/statistics" element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
      <Route path="/projects/add" element={<ProtectedRoute><AddProject /></ProtectedRoute>} />
      <Route path="/projects/:projectId/edit" element={<ProtectedRoute><EditProject /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
      <Route path="/shopping-list" element={<ProtectedRoute><ShoppingList /></ProtectedRoute>} />
      <Route path="/patterns" element={<ProtectedRoute><Patterns /></ProtectedRoute>} />
      <Route path="/patterns/add" element={<ProtectedRoute><AddPattern /></ProtectedRoute>} />
      <Route path="/patterns/:patternId" element={<ProtectedRoute><PatternDetail /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute><Marketplace /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
      <Route path="/support/thank-you" element={<ProtectedRoute><SupportThankYou /></ProtectedRoute>} />
      {/* Admin Routes */}
      <Route path="/admin/designs" element={<ProtectedRoute><AdminDesigns /></ProtectedRoute>} />
      <Route path="/admin/designs/new" element={<ProtectedRoute><AdminDesignForm /></ProtectedRoute>} />
      <Route path="/admin/designs/:designId/edit" element={<ProtectedRoute><AdminDesignForm /></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
      <Route path="/admin/blog/new" element={<ProtectedRoute><AdminBlogForm /></ProtectedRoute>} />
      <Route path="/admin/blog/:postId/edit" element={<ProtectedRoute><AdminBlogForm /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/translations" element={<ProtectedRoute><AdminTranslations /></ProtectedRoute>} />
      <Route path="/admin/legal" element={<ProtectedRoute><AdminLegalPages /></ProtectedRoute>} />
      {/* Public Stash */}
      <Route path="/stash/:userId" element={<PublicStash />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <FormErrorProvider>
        <BrowserRouter>
          <SkipLink />
          <AppRouter />
          <CookieConsent />
          <GoogleAnalytics />
          <Toaster />
        </BrowserRouter>
      </FormErrorProvider>
    </div>
  );
}

export default App;
